import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { InvoiceCard, SearchView } from "../../../components";
import { getInvoices, downloadInvoicesExcel } from "../../../data";
import ListView from "../../../components/list-view";
import SelectableListView from "../../../components/selectable-list-view";
import { useTableView } from "@/hooks/useTableView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  approveInvoice,
  downloadFilteredInvoicesAllExcel,
  downloadFilteredInvoicesExcel,
  getAllInvoices,
  getAllInvoicesOperations,
  uploadInvoiceStatusExcel,
  uploadBulkPaymentExcel,
  downloadFilteredInvoicesAllBankExcel,
  getPaymentHistoryGroups,
  getPaymentHistoryByGroupId,
  downloadGroupInvoicePdfs,
  getCommissionCountryGroupedInvoiceIds,
} from "@/data/requests/invoice-requests";
import { countryOptions } from "@/utils/country-options";
import { useToast } from "@/components/ui/use-toast";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import {
  getAllInvoicesOperationCommissions,
  downloadCommissionExcel,
  syncCommissionWithUser,
} from "@/data/requests/commission-requests";

const STATUS_OPTIONS = [
  { value: "Invoice Uploaded", label: "Fatura Yüklendi" },
  { value: "Mail Sended", label: "Mail Gönderildi" },
  { value: "Auto Created Invoice", label: "Otomatik Oluşturulan Fatura" },
  { value: "OCR Completed", label: "OCR Tamamlandı" },
  { value: "Change Requested", label: "Değişiklik İstendi" },
  { value: "all", label: "Tümü" },
];

export function InvoiceListOperationCommissionScreen() {
  const { isAdvancedTable } = useTableView();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const [pageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    name: "",
    email: "",
    status: "",
    country: "",
  });

  const navigate = useNavigate();

  const [page, setPage] = useState(() => {
    const storedPage = localStorage.getItem(
      `invoiceListPage-operations-commission`
    );
    return storedPage ? parseInt(storedPage, 10) : 1;
  });
  const [queryParams, setQueryParams] = useState(() => {
    const storedQueryParams = localStorage.getItem(
      `invoiceListQueryParams-operations-commission`
    );
    if (storedQueryParams && storedQueryParams !== "{}") {
      setSearchParams(JSON.parse(storedQueryParams));
    }
    return storedQueryParams ? JSON.parse(storedQueryParams) : {};
  });
  useEffect(() => {
    localStorage.setItem(
      `invoiceListPage-operations-commission`,
      page.toString()
    );
    localStorage.setItem(
      `invoiceListQueryParams-operations-commission`,
      JSON.stringify(queryParams)
    );
  }, [page, queryParams]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["invoices", page, pageSize, queryParams, sortColumn, sortDirection],
    queryFn: () =>
      getAllInvoicesOperationCommissions({
        page,
        pageSize,
        nameFilter: queryParams.name,
        emailNameFilter: queryParams.email,
        statusFilter: queryParams.status,
        countryFilter: queryParams.country,
        sortColumn,
        sortDirection,
      }),
    onError: (error) => {
      toast({
        title: "Faturalar yüklenirken hata oluştu",
        description:
          error.response?.data?.message || "Beklenmeyen bir hata oluştu",
        variant: "destructive",
      });
    },
  });

  const downloadExcel = () => {
    downloadCommissionExcel({
      nameFilter: queryParams.name,
      emailNameFilter: queryParams.email,
      statusFilter: queryParams.status,
      countryFilter: queryParams.country,
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "commissions.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
  };

  const handleSearch = (item) => {
    const newSearchParams = { ...searchParams, [item.key]: item.value };
    setSearchParams(newSearchParams);

    setTimeout(() => {
      setQueryParams(newSearchParams);
      setPage(1);
    }, 300);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return format(date, "dd.MM.yyyy HH:mm");
    } catch (error) {
      return dateString;
    }
  };

  const syncCommissionWithUserAsync = async () => {
    await syncCommissionWithUser().then((response) => {
      toast({
        title: "Komisyonlar kullanıcılarla eşleştirildi",
        description: response.data.message,
      });
    });
  };

  const pageSettings = {
    title: "Fatura Operasyonları Komisyonları",
    actions: [
      {
        name: "Komisyon Excel'i İndir",
        color: "bg-blue-500 hover:bg-blue-700 text-white",
        onClick: downloadExcel,
      },
    ],
  };

  // Eski tablo için basit kolonlar
  const simpleColumns = [
    {
      key: "influencerName",
      label: "Ad",
      accessor: (item) => item.influencerName || "",
    },
    {
      key: "email",
      label: "E-posta",
      accessor: (item) => item.email || "",
    },
    {
      key: "invoiceId",
      label: "Fatura Tarihimiz",
      accessor: (item) => item.invoiceId || "",
    },
    {
      key: "lucaAccountName",
      label: "Luca Hesap Adı",
      accessor: (item) => item.Client?.lucaAccountName || "",
    },
    {
      key: "lucaId",
      label: "Luca ID",
      accessor: (item) => item.Client?.lucaId || "",
    },
    {
      key: "finalAmount",
      label: "Net Tutar",
      accessor: (item) => item.finalAmount || "",
    },
    {
      key: "status",
      label: "Durum",
      render: (row) => (
        <div className="flex items-center gap-2">
          <span>{row.status}</span>
        </div>
      ),
    },
    {
      key: "actions",
      label: "İşlemler",
      render: (row) => <div className="flex gap-2"></div>,
    },
  ];

  // Gelişmiş tablo için tüm kolonlar
  const advancedColumns = [
    { key: "id", label: "ID", sortable: true },
    { key: "influencerName", label: "Influencer Adı", sortable: true },
    { key: "email", label: "E-posta", sortable: true },
    { key: "totalEarnings", label: "Toplam Kazanç", sortable: true },
    { key: "ourCommission", label: "Komisyonumuz", sortable: true },
    { key: "finalAmount", label: "Net Tutar", sortable: true },
    { key: "invoiceId", label: "Fatura No", sortable: true },
    { 
      key: "isMailSended", 
      label: "Mail Gönderildi", 
      sortable: true,
      accessor: (item) => item.isMailSended ? "Yes" : "No"
    },
    { key: "status", label: "Durum", sortable: true },
    { key: "managerName", label: "Menajer", sortable: true },
    { key: "country", label: "Ülke", sortable: true },
    { 
      key: "minAcceptedInvoiceDate", 
      label: "En Erken Fatura Tarihi", 
      sortable: true,
      accessor: (item) => item.minAcceptedInvoiceDate ? new Date(item.minAcceptedInvoiceDate).toLocaleDateString() : "-"
    },
    {
      key: "clientName",
      label: "Müşteri Adı",
      sortable: false,
      accessor: (item) => item.Client?.name || "",
    },
    {
      key: "clientEmail",
      label: "Müşteri E-postası",
      sortable: false,
      accessor: (item) => item.Client?.email || "",
    },
    {
      key: "billingName",
      label: "Fatura Adı",
      sortable: false,
      accessor: (item) => item.Client?.billingName || "",
    },
    {
      key: "lucaId",
      label: "Luca ID",
      sortable: false,
      accessor: (item) => item.Client?.lucaId || "",
    },
    {
      key: "lucaAccountName",
      label: "Luca Hesap Adı",
      sortable: false,
      accessor: (item) => item.Client?.lucaAccountName || "",
    },
    {
      key: "clientCurrency",
      label: "Müşteri Para Birimi",
      sortable: false,
      accessor: (item) => item.Client?.currency || "",
    },
    {
      key: "clientBankName",
      label: "Müşteri Bankası",
      sortable: false,
      accessor: (item) => item.Client?.bankName || "",
    },
    {
      key: "clientIBAN",
      label: "Müşteri IBAN",
      sortable: false,
      accessor: (item) => item.Client?.IBAN || "",
    },
    {
      key: "clientSWIFT",
      label: "Müşteri SWIFT",
      sortable: false,
      accessor: (item) => item.Client?.SWIFT || "",
    },
    {
      key: "newTvAccountManager",
      label: "NewTV Yöneticisi",
      sortable: false,
      accessor: (item) => item.Client?.newTvAccountManager || "",
    },
    { 
      key: "createdAt", 
      label: "Oluşturulma", 
      sortable: true,
      accessor: (item) => item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"
    },
    {
      key: "actions",
      label: "İşlemler",
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => navigate(`/payments/${row.paymentId}/commissions/${row.id}`)}>
            Görüntüle
          </Button>
        </div>
      ),
    },
  ];

  const defaultVisibleColumns = ["influencerName", "email", "finalAmount", "status", "country", "lucaId", "actions"];

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  return (
    <AppLayout
      fullscreen={true}
      title={pageSettings.title}
      actions={pageSettings.actions}
      loading={isLoading}
      style={{ padding: "0 10px" }}
    >
      <SearchView
        searchParams={searchParams}
        handleSearch={handleSearch}
        statusOptions={STATUS_OPTIONS}
        countryOptions={countryOptions}
        resetDefault={() => {
          setSearchParams({
            name: "",
            email: "",

            status: "",
            country: "",
          });
          setQueryParams({
            name: "",
            email: "",

            status: "",
            country: "",
          });

          setPage(1);
        }}
      />
      {isAdvancedTable ? (
        <SelectableListView
          isLoading={isLoading}
          data={data?.data?.commissions}
          columns={advancedColumns}
          columnVisibilityKey="invoices-operations-commission-columns"
          defaultVisibleColumnKeys={defaultVisibleColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSort}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/payments/${item.paymentId}/commissions/${item.id}`)}
        />
      ) : (
        <ListView
          isLoading={isLoading}
          data={data?.data?.commissions}
          columns={simpleColumns}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) => navigate(`/payments/${item.paymentId}/commissions/${item.id}`)}
          renderGridItem={(item) => (
            <InvoiceCard
              key={item.id}
              name={item.Client?.name}
              email={item.email}
              invoiceId={item.invoiceId}
              finalAmount={item.finalAmount}
              status={item.status}
              onDetailClick={() => navigate(`/payments/${item.paymentId}/commissions/${item.id}`)}
            />
          )}
        />
      )}
    </AppLayout>
  );
}
